import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Gavel, ArrowRightCircle, PauseCircle, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { confirmAction } from "@/components/app/Confirm";
import { api, get, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { stageLabel } from "@/lib/charts";
import { actions, nextStage } from "@/lib/candidateActions";
import { AssessmentLinksDialog } from "@/pages/assessments/AssessmentLinksDialog";

function SkipDialog({ app, open, onOpenChange, done }) {
  const [reason, setReason] = useState("");
  const go = async () => { try { await actions.skipWorkSample(app, reason); toast.success("Work sample skipped. Fit Score re-weighted for this candidate."); onOpenChange(false); done(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent data-testid="skip-ws-dialog">
      <DialogHeader><DialogTitle>Skip the work sample</DialogTitle></DialogHeader>
      <p className="text-sm text-ink-secondary">Their Fit Score will re-weight the other components for this candidate only.</p>
      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="skip-ws-reason" />
      <DialogFooter><Button disabled={reason.trim().length < 5} onClick={go} data-testid="skip-ws-confirm">Skip work sample</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

export function HeaderActions({ app, c, canEdit, onDecide }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [skip, setSkip] = useState(false);
  const [links, setLinks] = useState(null);
  const copyLink = async () => {
    try {
      const { data: r } = await actions.assessmentLink(app);
      if (r.links.length) { setLinks(r); refresh(); } else toast.error(r.skipped[0]?.reason || "No link created");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const { data } = useQuery({ queryKey: ["app-fit", app.id], queryFn: () => get(`/applications/${app.id}/fit`) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["candidate"] }); qc.invalidateQueries({ queryKey: ["app-fit", app.id] }); };
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const to = nextStage(app, app.work_sample_enabled);
  const del = async () => {
    const ok = await confirmAction({ title: "Delete this candidate's data?", body: "Personal data is removed from every job, assessment and interview. Hired and synced records are kept. This can't be undone.",
      entity: `${c.display_name} · ${c.applications.length} application(s)`, typeToConfirm: "DELETE", confirmLabel: "Delete data" });
    if (!ok) return;
    try { await api.post(`/candidates/${c.id}/anonymise`); toast.success("Candidate data deleted"); qc.invalidateQueries(); nav("/app/candidates"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="header-actions">
      {canEdit && to && <Button onClick={() => run(() => actions.advance(app, to), `Moved to ${stageLabel(to)}`)} data-testid="action-advance"><ArrowRightCircle size={16} /> Advance to {stageLabel(to)}</Button>}
      {data?.can_decide && <Button variant="outline" onClick={() => onDecide("hold")} data-testid="action-hold"><PauseCircle size={16} /> Hold</Button>}
      {data?.can_decide && <Button variant="outline" className="text-risk hover:text-risk" onClick={() => onDecide("reject")} data-testid="action-reject"><XCircle size={16} /> Reject</Button>}
      {data?.can_decide && <Button variant="outline" onClick={() => onDecide()} data-testid="action-decide"><Gavel size={16} /> Decide</Button>}
      {canEdit && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="outline" data-testid="action-send-menu">Send <ChevronDown size={14} /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-[6px]">
            <DropdownMenuItem onClick={() => run(() => actions.sendAssessment(app), "Assessment invite sent")} data-testid="action-send-assessment">Send assessment by email</DropdownMenuItem>
            <DropdownMenuItem onClick={copyLink} data-testid="action-copy-assessment-link">Copy assessment link</DropdownMenuItem>
            {app.work_sample_enabled && <DropdownMenuItem onClick={() => run(() => actions.sendWorkSample(app), "Work sample sent")} data-testid="action-send-work-sample">Send work sample</DropdownMenuItem>}
            {app.work_sample_enabled && <DropdownMenuItem onClick={() => setSkip(true)} data-testid="action-skip-work-sample">Skip work sample</DropdownMenuItem>}
            <DropdownMenuItem onClick={() => run(async () => { await actions.prepareInterview(app); nav(`/app/candidates/${app.candidate_id}/interview`); }, "Interview plan is being prepared")} data-testid="action-send-interview">Send interview</DropdownMenuItem>
            {user.role === "super_admin" && <><DropdownMenuSeparator /><DropdownMenuItem className="text-risk" onClick={del} data-testid="action-delete-candidate"><Trash2 size={14} /> Delete candidate data</DropdownMenuItem></>}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <AssessmentLinksDialog result={links} onClose={() => setLinks(null)} />
      {skip && <SkipDialog app={app} open={skip} onOpenChange={setSkip} done={refresh} />}
    </div>
  );
}
